import { ViewModel } from "mvvm/ViewModel";
import { JobViewModel } from "./JobViewModel";
import { PageViewModel } from "./PageViewModel";

const getJobIDFromReplyTarget = (replyTarget?: string) => {
    if (!replyTarget) return undefined;
    const parts = replyTarget.split("-");
    if (parts.length < 2) return undefined;
    return parts.slice(1).join("-");
};

export class QuestionViewModel extends ViewModel {

    private _inputText: string = "";
    private _selectedJob: JobViewModel | undefined = undefined;
    private _jobUnsubscribe: (() => void) | undefined = undefined;

    constructor(public readonly page: PageViewModel) {
        super();
    }

    get inputText() {
        return this._inputText;
    }

    set inputText(value: string) {
        if (this._inputText !== value) {
            this._inputText = value;
            this.updateViews();
        }
    }

    get selectedJob() {
        return this._selectedJob;
    }

    set selectedJob(value: JobViewModel | undefined) {
        if (this._selectedJob !== value) {
            // Unsubscribe from previous job's updates
            if (this._jobUnsubscribe) {
                this._jobUnsubscribe();
                this._jobUnsubscribe = undefined;
            }

            this._selectedJob = value;

            // Subscribe to new job's updates
            if (value) {
                const callback = () => {
                    this.updateViews();
                };
                value.addView(callback);
                this._jobUnsubscribe = () => value.removeView(callback);
            }

            this.updateViews();
        }
    }

    get submitButtonCaption() {
        return this.isSubmitting ? "Submitting" : "Submit";
    }

    get canSubmit() {
        return !this.isSubmitting && this.hasInput; // && (!this.selectedJob || this.selectedJob.status !== "RUNNING" || this._isDirty);
    }

    get hasInput() {
        return !!this.inputText.trim();
    }

    get canReply() {
        const targetJob = this.replyTargetJob;
        return (
            this.canSubmit &&
            targetJob !== undefined &&
            targetJob?.outputState.status === "loaded" &&
            targetJob.outputState.output.Conversation !== undefined
        )
    }

    async reply(memory: string, email?: string) {
        const targetJob = this.replyTargetJob;
        if (this.canReply && targetJob && targetJob.outputState.status === "loaded") {
            this.isSubmitting = true;
            const jobInfo = await this.page.jobService.createJob(this.page.modelAlias, this._inputText, targetJob.jobID, targetJob.question, memory, undefined, undefined, email);
            this.page.addJob(jobInfo);
            this.clearContent();
            this.isSubmitting = false;
        }
    }

    async submit(memory: string, email?: string) {
        if (this.canSubmit) {
            this.isSubmitting = true;
            const jobInfo = await this.page.jobService.createJob(this.page.modelAlias, this._inputText, '', '', memory, undefined, undefined, email);
            this.page.addJob(jobInfo);
            this.clearContent();
            this.isSubmitting = false;
        }
    }

    get isSubmitting() {
        return this.page.isSubmitting;
    }

    private set isSubmitting(value: boolean) {
        if (this.page.isSubmitting !== value) {
            this.page.isSubmitting = value;
            this.updateViews();
        }
    }

    get canClear() {
        return !this.isSubmitting && (this.hasInput || this.selectedJob !== undefined);
    }

    clearContent() {
        this.inputText = "";
    }

    reset() {
        this.inputText = "";
        this.page.selectedJob = undefined;
    }

    get replyTargetJob(): JobViewModel | undefined {
        if (!this.selectedJob) return undefined;
        const threadJobs = this.buildThreadJobs(this.selectedJob);
        return threadJobs.length > 0
            ? threadJobs[threadJobs.length - 1]
            : this.selectedJob;
    }

    private buildThreadJobs(selectedJob: JobViewModel) {
        const jobs = this.page.jobs;
        const jobMap = new Map<string, JobViewModel>();
        jobs.forEach(job => jobMap.set(job.jobID, job));

        let rootJob: JobViewModel = selectedJob;
        const visited = new Set<string>();
        let parentID = getJobIDFromReplyTarget(selectedJob.replyTarget);
        while (parentID) {
            if (visited.has(parentID)) break;
            visited.add(parentID);
            const parentJob = jobMap.get(parentID);
            if (!parentJob) break;
            rootJob = parentJob;
            parentID = getJobIDFromReplyTarget(parentJob.replyTarget);
        }

        const childrenMap = new Map<string, JobViewModel[]>();
        jobs.forEach(job => {
            const parentID = getJobIDFromReplyTarget(job.replyTarget);
            if (!parentID) return;
            if (!childrenMap.has(parentID)) {
                childrenMap.set(parentID, []);
            }
            childrenMap.get(parentID)!.push(job);
        });

        childrenMap.forEach(children => children.sort((a, b) => a.startTime - b.startTime));

        const ordered: JobViewModel[] = [];
        const traverse = (job: JobViewModel) => {
            ordered.push(job);
            const children = childrenMap.get(job.jobID) || [];
            children.forEach(traverse);
        };
        traverse(rootJob);
        return ordered;
    }

    dispose() {
        if (this._jobUnsubscribe) {
            this._jobUnsubscribe();
            this._jobUnsubscribe = undefined;
        }
    }
}